/**
 * Task 245 — pick follow-up passages to offer after an answer.
 *
 * Source: `graph-data/related.json`, a map of `book-chapter` → top affirmed
 * neighbours. AFFIRMED only: every edge in that file was judged to represent a
 * relation Koresh actually teaches, not mere co-occurrence. Suggesting a
 * connection he never made would be worse than suggesting nothing.
 *
 * Pure — no fetch, no DOM — so the selection rules are smoke-tested directly.
 */

export interface RelatedFile {
  schema?: unknown;
  top_n?: unknown;
  /** slug -> accented display name, emitted with the artifact so the chat and
   *  the library spell a book the same way ("Isaías", never "Isaias"). */
  books?: Record<string, unknown>;
  related?: Record<string, unknown>;
}

/** A `book-chapter` key as it appears in the artifact, split for display. */
export interface RelatedPassage {
  key: string;
  book: string;
  chapter: number;
  /** Accented book name when the artifact supplies one. */
  bookName?: string;
  /** Actual origin of this edge, never the first unrelated source in an answer. */
  from?: { book: string; chapter: number; bookName?: string };
}

/** `"apocalipsis-12"` → `{book:"apocalipsis", chapter:12}`; null when malformed. */
export function parsePassageKey(key: string): RelatedPassage | null {
  const cut = key.lastIndexOf("-");
  if (cut <= 0) return null;
  const book = key.slice(0, cut);
  const chapter = Number.parseInt(key.slice(cut + 1), 10);
  if (book.length === 0 || !Number.isInteger(chapter) || chapter <= 0) return null;
  return { key, book, chapter };
}

/** The chapters an answer cited, as artifact keys, in citation order. */
export function citedChapterKeys(
  sources: ReadonlyArray<{ book?: string | undefined; chapter?: number | undefined }>,
): string[] {
  const out: string[] = [];
  for (const s of sources) {
    if (typeof s.book !== "string" || typeof s.chapter !== "number") continue;
    const key = `${s.book}-${s.chapter}`;
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

/**
 * Follow-ups for an answer.
 *
 * - walks the cited chapters in citation order, so the most-cited passage's
 *   connections surface first;
 * - EXCLUDES anything the answer already cited — offering "connects with Juan 4"
 *   when Juan 4 is citation [1] is noise, not a next step;
 * - EXCLUDES `alreadyOffered` — see pickRelatedForThread;
 * - dedupes across cited chapters;
 * - returns at most `limit`, and `[]` when the artifact is missing or empty
 *   (the caller renders nothing).
 */
export function pickRelated(
  citedKeys: readonly string[],
  file: RelatedFile | null,
  limit: number,
  alreadyOffered?: Iterable<string>,
): RelatedPassage[] {
  if (file === null || typeof file.related !== "object" || file.related === null) return [];
  const seen = new Set<string>(citedKeys);
  if (alreadyOffered !== undefined) for (const k of alreadyOffered) seen.add(k);
  const out: RelatedPassage[] = [];

  for (const cited of citedKeys) {
    const neighbours = file.related[cited];
    if (!Array.isArray(neighbours)) continue;
    for (const raw of neighbours) {
      if (out.length >= limit) return out;
      if (typeof raw !== "string" || seen.has(raw)) continue;
      const parsed = parsePassageKey(raw);
      if (parsed === null) continue;
      seen.add(raw);
      const name = file.books?.[parsed.book];
      const origin = parsePassageKey(cited);
      if (origin === null) continue;
      const originName = file.books?.[origin.book];
      out.push({ ...parsed, ...(typeof name === "string" ? { bookName: name } : {}),
        from: { book: origin.book, chapter: origin.chapter,
          ...(typeof originName === "string" ? { bookName: originName } : {}) } });
    }
  }
  return out;
}

/**
 * Follow-ups for a whole conversation — one array per assistant turn, in order.
 *
 * Per-turn selection alone is not enough, because a chip the reader FOLLOWS does
 * not become a citation. Ask "¿Cómo conecta Apocalipsis 12 con Salmos 110?" and
 * the evidence still comes back as Apocalipsis 12 chunks; Salmos 110 is never
 * cited, so the next turn re-derives Apocalipsis 12's neighbours and offers
 * Salmos 110 again — to the one reader we know already took it. Observed live.
 *
 * So the exclusion set is the conversation's memory: nothing offered in an
 * earlier turn is offered again. Each answer opens new doors rather than
 * re-showing the same three, and a passage dropped this way is still reachable
 * from the library and the connections graph.
 */
export function pickRelatedForThread(
  citedPerTurn: ReadonlyArray<readonly string[]>,
  file: RelatedFile | null,
  limit: number,
): RelatedPassage[][] {
  const offered = new Set<string>();
  return citedPerTurn.map((cited) => {
    const picks = pickRelated(cited, file, limit, offered);
    for (const p of picks) offered.add(p.key);
    return picks;
  });
}

/** Chapter-level graph edges do not establish relevance to a topical answer.
 * Only offer them for passages explicitly requested AND actually cited inline.
 * In particular a wealth/poverty query must not inherit unrelated Jubilees edges.
 */
export function scopedRelatedChapterKeys(answer: string,
  sources: ReadonlyArray<{ book?: string; chapter?: number }>,
  references: ReadonlyArray<{ book: string; chapter: number }>,
): string[] {
  const requested = new Set(citedChapterKeys(references));
  const used = [...answer.matchAll(/\[(\d+)\]/g)]
    .map(match => sources[Number(match[1]) - 1])
    .filter((source): source is { book?: string; chapter?: number } => source !== undefined);
  return citedChapterKeys(used).filter(key => requested.has(key));
}
